import type { ApiResponse } from '@/types/api.types'
import type { Assessment } from '@/types/assessment.types'
import type {
  AssessmentProcessTree,
  AssessmentWorkflowAdvanceCheck,
  AssessmentWorkflowAdvanceResult,
  Evaluation,
  EvaluationCreate,
  EvaluationUpdate,
  RecomendacionAssessment,
  RecomendacionAssessmentCreate,
  RecomendacionAssessmentUpdate,
  SugerenciasObservacionObjetivoResponse
} from '@/types/assessment_process.types'

import { apiClient, ensureValidToken } from '../client'

export class AssessmentProcessService {
  // =============================================================================
  // ASSESSMENT PROCESS WORKFLOW ENDPOINTS
  // =============================================================================

  async getAssessmentProcessById(assessmentId: string): Promise<AssessmentProcessTree> {
    await ensureValidToken()

    const response = await apiClient.get<ApiResponse<AssessmentProcessTree>>(
      `/api/v1/assessments/${assessmentId}/process`
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid get assessment process response')
  }

  async canAdvancePhase(assessmentId: string): Promise<AssessmentWorkflowAdvanceCheck> {
    await ensureValidToken()

    const response = await apiClient.get<ApiResponse<boolean>>(
      `/api/v1/assessments/${assessmentId}/process/can-advance-phase`
    )

    if (response.data && typeof response.data.data === 'boolean') {
      return {
        success: true,
        can_advance: response.data.data,
        message: response.data.data ? 'Can advance to next phase' : 'Cannot advance yet'
      }
    }

    throw new Error('Invalid can advance phase response')
  }

  async advancePhase(assessmentId: string): Promise<AssessmentWorkflowAdvanceResult> {
    await ensureValidToken()

    const response = await apiClient.post<ApiResponse<Assessment>>(
      `/api/v1/assessments/${assessmentId}/process/advance-phase`
    )

    if (response.data && response.data.data) {
      return {
        success: true,
        message: 'Phase advanced successfully',
        assessment: response.data.data
      }
    }

    throw new Error('Invalid advance phase response')
  }

  // =============================================================================
  // MATURITY EVALUATION ENDPOINTS
  // =============================================================================

  async getEvaluations(assessmentId: string): Promise<Evaluation[]> {
    await ensureValidToken()

    const response = await apiClient.get<ApiResponse<Evaluation[]>>(
      `/api/v1/assessments/${assessmentId}/evaluaciones`
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid get evaluations response')
  }

  async createEvaluation(
    assessmentId: string,
    evaluationData: EvaluationCreate
  ): Promise<Evaluation> {
    await ensureValidToken()

    const response = await apiClient.post<ApiResponse<Evaluation>>(
      `/api/v1/assessments/${assessmentId}/evaluaciones`,
      evaluationData
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid create evaluation response')
  }

  async getEvaluationById(assessmentId: string, evaluationId: string): Promise<Evaluation> {
    await ensureValidToken()

    const response = await apiClient.get<ApiResponse<Evaluation>>(
      `/api/v1/assessments/${assessmentId}/evaluaciones/${evaluationId}`
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid get evaluation response')
  }

  async updateEvaluation(
    assessmentId: string,
    evaluationId: string,
    evaluationData: EvaluationUpdate
  ): Promise<Evaluation> {
    await ensureValidToken()

    const response = await apiClient.put<ApiResponse<Evaluation>>(
      `/api/v1/assessments/${assessmentId}/evaluaciones/${evaluationId}`,
      evaluationData
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid update evaluation response')
  }

  async getSugerenciasObservacionObjetivo(
    assessmentId: string,
    codigoSubcategoria: string,
    nivelObjetivo: number,
    limit?: number
  ): Promise<SugerenciasObservacionObjetivoResponse> {
    await ensureValidToken()

    const params = new URLSearchParams({
      codigo_subcategoria: codigoSubcategoria,
      nivel_objetivo: String(nivelObjetivo)
    })
    if (limit !== undefined) {
      params.set('limit', String(limit))
    }

    const response = await apiClient.get<ApiResponse<SugerenciasObservacionObjetivoResponse>>(
      `/api/v1/assessments/${assessmentId}/evaluaciones/sugerencias-observacion-objetivo?${params.toString()}`
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid get sugerencias observacion objetivo response')
  }

  // =============================================================================
  // ASSESSMENT RECOMMENDATIONS ENDPOINTS
  // =============================================================================

  async getRecommendations(assessmentId: string): Promise<RecomendacionAssessment[]> {
    await ensureValidToken()

    const response = await apiClient.get<ApiResponse<RecomendacionAssessment[]>>(
      `/api/v1/assessments/${assessmentId}/recomendaciones`
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid get recommendations response')
  }

  async createRecommendation(
    assessmentId: string,
    recommendationData: RecomendacionAssessmentCreate
  ): Promise<RecomendacionAssessment> {
    await ensureValidToken()

    const response = await apiClient.post<ApiResponse<RecomendacionAssessment>>(
      `/api/v1/assessments/${assessmentId}/recomendaciones`,
      recommendationData
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid create recommendation response')
  }

  async getRecommendationById(
    assessmentId: string,
    recommendationId: string
  ): Promise<RecomendacionAssessment> {
    await ensureValidToken()

    const response = await apiClient.get<ApiResponse<RecomendacionAssessment>>(
      `/api/v1/assessments/${assessmentId}/recomendaciones/${recommendationId}`
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid get recommendation response')
  }

  async updateRecommendation(
    assessmentId: string,
    recommendationId: string,
    recommendationData: RecomendacionAssessmentUpdate
  ): Promise<RecomendacionAssessment> {
    await ensureValidToken()

    const response = await apiClient.put<ApiResponse<RecomendacionAssessment>>(
      `/api/v1/assessments/${assessmentId}/recomendaciones/${recommendationId}`,
      recommendationData
    )

    if (response.data && response.data.data) {
      return response.data.data
    }

    throw new Error('Invalid update recommendation response')
  }

  async deleteRecommendation(assessmentId: string, recommendationId: string): Promise<void> {
    await ensureValidToken()

    await apiClient.delete(
      `/api/v1/assessments/${assessmentId}/recomendaciones/${recommendationId}`
    )
  }
}

export const assessmentProcessService = new AssessmentProcessService()
